import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import axiosInstance from "../axiosInstance";
import AuthService from "../AuthService";
import IctResourcesPanel from "./IctResourcesPanel";

const tabs = [
  { key: "resources", label: "Class Resources", icon: "bi-folder2-open" },
  { key: "dashboard", label: "Dashboard", icon: "bi-grid" },
  { key: "projects", label: "Projects", icon: "bi-kanban" },
  { key: "submissions", label: "Submissions", icon: "bi-upload" },
  { key: "skills", label: "Skills Tracking", icon: "bi-bar-chart-steps" },
  { key: "equipment", label: "Equipment", icon: "bi-hdd-network" },
  { key: "sessions", label: "Lab Sessions", icon: "bi-calendar2-event" },
];

const emptyCategory = { id: null, name: "", code: "", module_type: "ICT", description: "", is_active: true };
const emptyProject = {
  id: null,
  title: "",
  project_type: "ICT",
  category: "",
  academic_year: "",
  term: "",
  class_group: "",
  class_subject: "",
  teacher: "",
  instructions: "",
  external_url: "",
  start_date: "",
  due_date: "",
  max_score: "100",
  status: "DRAFT",
};
const emptySubmission = {
  id: null,
  project: "",
  student: "",
  status: "NOT_SUBMITTED",
  submission_url: "",
  notes: "",
  score: "",
  teacher_comment: "",
};
const emptySkill = { id: null, name: "", code: "", skill_area: "ICT", description: "", is_active: true };
const emptyProgress = {
  id: null,
  student: "",
  class_group: "",
  skill: "",
  level: "NOT_STARTED",
  teacher: "",
  evidence: "",
  notes: "",
  assessed_at: "",
};
const emptyEquipment = {
  id: null,
  name: "",
  category: "OTHER",
  asset_tag: "",
  serial_number: "",
  condition: "",
  status: "AVAILABLE",
  assigned_teacher: "",
  assigned_class_group: "",
  purchase_date: "",
  notes: "",
};
const emptySession = {
  id: null,
  title: "",
  class_group: "",
  class_subject: "",
  project: "",
  teacher: "",
  lab_room: "",
  session_date: "",
  start_time: "",
  end_time: "",
  status: "PLANNED",
  equipment: [],
  objectives: "",
  session_notes: "",
};

function ICTRobotics() {
  const currentUser = AuthService.getCurrentUser();
  const isTeacher = currentUser?.user_type === "TEACHER";

  const [activeTab, setActiveTab] = useState("dashboard");
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState({});
  const [categories, setCategories] = useState([]);
  const [projects, setProjects] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [skills, setSkills] = useState([]);
  const [progressRecords, setProgressRecords] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [labSessions, setLabSessions] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [terms, setTerms] = useState([]);
  const [classGroups, setClassGroups] = useState([]);
  const [classSubjects, setClassSubjects] = useState([]);
  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [categoryForm, setCategoryForm] = useState(emptyCategory);
  const [projectForm, setProjectForm] = useState(() => ({ ...emptyProject, teacher: isTeacher ? currentUser?.id || "" : "" }));
  const [submissionForm, setSubmissionForm] = useState(emptySubmission);
  const [skillForm, setSkillForm] = useState(emptySkill);
  const [progressForm, setProgressForm] = useState(() => ({ ...emptyProgress, teacher: isTeacher ? currentUser?.id || "" : "" }));
  const [equipmentForm, setEquipmentForm] = useState(emptyEquipment);
  const [sessionForm, setSessionForm] = useState(() => ({ ...emptySession, teacher: isTeacher ? currentUser?.id || "" : "" }));
  const [projectFile, setProjectFile] = useState(null);
  const [submissionFile, setSubmissionFile] = useState(null);
  const [search, setSearch] = useState("");

  const filteredClassSubjects = useMemo(() => {
    if (!projectForm.class_group) return classSubjects;
    return classSubjects.filter((item) => String(item.class_group) === String(projectForm.class_group));
  }, [classSubjects, projectForm.class_group]);

  const filteredSessionSubjects = useMemo(() => {
    if (!sessionForm.class_group) return classSubjects;
    return classSubjects.filter((item) => String(item.class_group) === String(sessionForm.class_group));
  }, [classSubjects, sessionForm.class_group]);

  const filteredStudents = useMemo(() => {
    const selectedProject = projects.find((item) => String(item.id) === String(submissionForm.project));
    if (!selectedProject) return students;
    return students.filter((student) => String(student.class_group) === String(selectedProject.class_group));
  }, [students, projects, submissionForm.project]);

  const visibleProjects = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return projects;
    return projects.filter((item) =>
      [item.title, item.project_type_label, item.class_group_name, item.subject_name, item.teacher_name]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query))
    );
  }, [projects, search]);

  const loadAll = async () => {
    try {
      setLoading(true);
      await axiosInstance.post("ict-robotics/bootstrap/");
      const [
        summaryResponse,
        categoriesResponse,
        projectsResponse,
        submissionsResponse,
        skillsResponse,
        progressResponse,
        equipmentResponse,
        sessionsResponse,
        yearsResponse,
        termsResponse,
        groupsResponse,
        optionsResponse,
        teachersResponse,
      ] = await Promise.all([
        axiosInstance.get("ict-robotics/summary/"),
        axiosInstance.get("ict-robotics/categories/"),
        axiosInstance.get("ict-robotics/projects/"),
        axiosInstance.get("ict-robotics/submissions/"),
        axiosInstance.get("ict-robotics/skills/"),
        axiosInstance.get("ict-robotics/student-skills/"),
        axiosInstance.get("ict-robotics/equipment/"),
        axiosInstance.get("ict-robotics/lab-sessions/"),
        axiosInstance.get("academic/years/"),
        axiosInstance.get("academic/terms/"),
        axiosInstance.get("academic/class-groups/"),
        axiosInstance.get("ict-robotics/options/"),
        axiosInstance.get("academic/setup/teachers/"),
      ]);

      setSummary(summaryResponse.data || {});
      setCategories(categoriesResponse.data || []);
      setProjects(projectsResponse.data || []);
      setSubmissions(submissionsResponse.data || []);
      setSkills(skillsResponse.data || []);
      setProgressRecords(progressResponse.data || []);
      setEquipment(equipmentResponse.data || []);
      setLabSessions(sessionsResponse.data || []);
      setAcademicYears(yearsResponse.data || []);
      setTerms(termsResponse.data || []);
      setClassGroups(groupsResponse.data || []);
      setClassSubjects(optionsResponse.data?.class_subjects || []);
      setStudents(optionsResponse.data?.students || []);
      setTeachers(teachersResponse.data || []);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load ICT & Robotics module.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const saveCategory = async (event) => {
    event.preventDefault();
    await saveJsonRecord(categoryForm, setCategoryForm, emptyCategory, "ict-robotics/categories/", "Category saved.");
  };

  const saveProject = async (event) => {
    event.preventDefault();
    try {
      setLoading(true);
      const payload = new FormData();
      Object.entries(projectForm).forEach(([key, value]) => {
        if (key !== "id" && value !== "" && value !== null && value !== undefined) payload.append(key, value);
      });
      if (projectFile) payload.append("attachment", projectFile);
      const config = { headers: { "Content-Type": "multipart/form-data" } };
      if (projectForm.id) {
        await axiosInstance.patch(`ict-robotics/projects/${projectForm.id}/`, payload, config);
        toast.success("Project updated.");
      } else {
        await axiosInstance.post("ict-robotics/projects/", payload, config);
        toast.success("Project added.");
      }
      setProjectForm({ ...emptyProject, teacher: isTeacher ? currentUser?.id || "" : "" });
      setProjectFile(null);
      await loadAll();
    } catch (error) {
      showError(error, "Failed to save project.");
    } finally {
      setLoading(false);
    }
  };

  const saveSubmission = async (event) => {
    event.preventDefault();
    try {
      setLoading(true);
      const payload = new FormData();
      Object.entries(submissionForm).forEach(([key, value]) => {
        if (key !== "id" && value !== "" && value !== null && value !== undefined) payload.append(key, value);
      });
      if (submissionFile) payload.append("submitted_file", submissionFile);
      const config = { headers: { "Content-Type": "multipart/form-data" } };
      if (submissionForm.id) {
        await axiosInstance.patch(`ict-robotics/submissions/${submissionForm.id}/`, payload, config);
        toast.success("Submission updated.");
      } else {
        await axiosInstance.post("ict-robotics/submissions/", payload, config);
        toast.success("Submission recorded.");
      }
      setSubmissionForm(emptySubmission);
      setSubmissionFile(null);
      await loadAll();
    } catch (error) {
      showError(error, "Failed to save submission.");
    } finally {
      setLoading(false);
    }
  };

  const saveSkill = async (event) => {
    event.preventDefault();
    await saveJsonRecord(skillForm, setSkillForm, emptySkill, "ict-robotics/skills/", "Skill saved.");
  };

  const saveProgress = async (event) => {
    event.preventDefault();
    await saveJsonRecord(progressForm, setProgressForm, { ...emptyProgress, teacher: isTeacher ? currentUser?.id || "" : "" }, "ict-robotics/student-skills/", "Skill progress saved.");
  };

  const saveEquipment = async (event) => {
    event.preventDefault();
    await saveJsonRecord(equipmentForm, setEquipmentForm, emptyEquipment, "ict-robotics/equipment/", "Equipment saved.");
  };

  const saveSession = async (event) => {
    event.preventDefault();
    await saveJsonRecord(sessionForm, setSessionForm, { ...emptySession, teacher: isTeacher ? currentUser?.id || "" : "" }, "ict-robotics/lab-sessions/", "Lab session saved.");
  };

  const saveJsonRecord = async (form, setForm, emptyForm, endpoint, successMessage) => {
    try {
      setLoading(true);
      const payload = cleanPayload(form);
      if (form.id) {
        await axiosInstance.patch(`${endpoint}${form.id}/`, payload);
        toast.success("Record updated.");
      } else {
        await axiosInstance.post(endpoint, payload);
        toast.success(successMessage);
      }
      setForm(emptyForm);
      await loadAll();
    } catch (error) {
      showError(error, "Failed to save record.");
    } finally {
      setLoading(false);
    }
  };

  const deleteRecord = async (url, label) => {
    if (!window.confirm(`Delete ${label}?`)) return;
    try {
      setLoading(true);
      await axiosInstance.delete(url);
      toast.success(`${label} deleted.`);
      await loadAll();
    } catch (error) {
      toast.error(`Could not delete ${label}. It may already be in use.`);
    } finally {
      setLoading(false);
    }
  };

  const editProject = (project) => {
    setProjectForm({
      id: project.id,
      title: project.title || "",
      project_type: project.project_type || "ICT",
      category: project.category || "",
      academic_year: project.academic_year || "",
      term: project.term || "",
      class_group: project.class_group || "",
      class_subject: project.class_subject || "",
      teacher: project.teacher || "",
      instructions: project.instructions || "",
      external_url: project.external_url || "",
      start_date: project.start_date || "",
      due_date: project.due_date || "",
      max_score: project.max_score || "100",
      status: project.status || "DRAFT",
    });
    setProjectFile(null);
    setActiveTab("projects");
  };

  const editSubmission = (submission) => {
    setSubmissionForm({
      id: submission.id,
      project: submission.project || "",
      student: submission.student || "",
      status: submission.status || "NOT_SUBMITTED",
      submission_url: submission.submission_url || "",
      notes: submission.notes || "",
      score: submission.score || "",
      teacher_comment: submission.teacher_comment || "",
    });
    setSubmissionFile(null);
    setActiveTab("submissions");
  };

  const editProgress = (record) => {
    setProgressForm({
      id: record.id,
      student: record.student || "",
      class_group: record.class_group || "",
      skill: record.skill || "",
      level: record.level || "NOT_STARTED",
      teacher: record.teacher || "",
      evidence: record.evidence || "",
      notes: record.notes || "",
      assessed_at: record.assessed_at || "",
    });
    setActiveTab("skills");
  };

  const editSession = (session) => {
    setSessionForm({
      id: session.id,
      title: session.title || "",
      class_group: session.class_group || "",
      class_subject: session.class_subject || "",
      project: session.project || "",
      teacher: session.teacher || "",
      lab_room: session.lab_room || "",
      session_date: session.session_date || "",
      start_time: session.start_time || "",
      end_time: session.end_time || "",
      status: session.status || "PLANNED",
      equipment: session.equipment || [],
      objectives: session.objectives || "",
      session_notes: session.session_notes || "",
    });
    setActiveTab("sessions");
  };

  return (
    <div className="container-fluid py-4">
      <div className="card border-0 shadow-sm mb-3">
        <div className="card-body">
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
            <div>
              <h3 className="fw-bold mb-1" style={{ color: "#125b2d" }}>ICT & Robotics</h3>
              <p className="text-muted mb-0">Manage practical projects, robotics equipment, lab sessions, submissions, and student ICT skills.</p>
            </div>
            <button className="btn btn-outline-success" onClick={loadAll} disabled={loading}>
              <i className="bi bi-arrow-clockwise me-2"></i>
              Refresh
            </button>
          </div>
          <div className="row g-2 mt-3">
            <Summary label="Active Projects" value={summary.active_projects || 0} />
            <Summary label="Submissions" value={summary.submissions || 0} />
            <Summary label="Equipment" value={summary.equipment || 0} />
            <Summary label="Upcoming Labs" value={summary.upcoming_sessions || 0} />
          </div>
        </div>
      </div>

      <div className="d-flex flex-wrap gap-2 mb-3">
        {tabs.map((tab) => (
          <button key={tab.key} type="button" className={`btn btn-sm ${activeTab === tab.key ? "btn-success" : "btn-light border"}`} onClick={() => setActiveTab(tab.key)}>
            <i className={`bi ${tab.icon} me-2`}></i>{tab.label}
          </button>
        ))}
      </div>

      {activeTab === "resources" && <IctResourcesPanel />}

      {activeTab === "dashboard" && (
        <div className="row g-3">
          <div className="col-lg-8">
            <SetupCard title="Recent ICT & Robotics Projects">
              <div className="d-flex mb-3">
                <input className="form-control" placeholder="Search projects..." value={search} onChange={(event) => setSearch(event.target.value)} />
              </div>
              <SimpleTable
                headers={["Project", "Class", "Subject", "Teacher", "Status", "Due"]}
                rows={visibleProjects.slice(0, 8).map((project) => [
                  project.title,
                  project.class_group_name,
                  project.subject_name,
                  project.teacher_name || "-",
                  <Status value={project.status_label || project.status} />,
                  project.due_date || "-",
                ])}
              />
            </SetupCard>
          </div>
          <div className="col-lg-4">
            <SetupCard title="Module Coverage">
              <div className="list-group list-group-flush">
                <DashboardLine label="Categories" value={summary.categories || 0} />
                <DashboardLine label="Skills" value={summary.skills || 0} />
                <DashboardLine label="Skill Records" value={summary.skill_records || 0} />
                <DashboardLine label="Reviewed Submissions" value={summary.reviewed_submissions || 0} />
                <DashboardLine label="Available Equipment" value={summary.equipment_available || 0} />
              </div>
            </SetupCard>
          </div>
        </div>
      )}

      {activeTab === "projects" && (
        <SetupCard title="Practical Projects">
          <form className="row g-3 mb-4" onSubmit={saveProject}>
            <TextField label="Project Title" value={projectForm.title} onChange={(value) => setProjectForm((prev) => ({ ...prev, title: value }))} required />
            <SelectField label="Project Type" value={projectForm.project_type} onChange={(value) => setProjectForm((prev) => ({ ...prev, project_type: value }))} options={[["ICT", "ICT"], ["ROBOTICS", "Robotics"], ["CODING", "Coding"], ["DIGITAL_LITERACY", "Digital Literacy"], ["STEM", "STEM"]]} />
            <SelectField label="Category" value={projectForm.category} onChange={(value) => setProjectForm((prev) => ({ ...prev, category: value }))} options={categories.filter((item) => item.is_active).map((item) => [item.id, item.name])} required={false} />
            <SelectField label="Academic Year" value={projectForm.academic_year} onChange={(value) => setProjectForm((prev) => ({ ...prev, academic_year: value }))} options={academicYears.map((item) => [item.id, item.name])} required={false} />
            <SelectField label="Term" value={projectForm.term} onChange={(value) => setProjectForm((prev) => ({ ...prev, term: value }))} options={terms.map((item) => [item.id, item.academic_year_name ? `${item.academic_year_name} - ${item.name}` : item.name])} required={false} />
            <SelectField label="Class Group" value={projectForm.class_group} onChange={(value) => setProjectForm((prev) => ({ ...prev, class_group: value, class_subject: "" }))} options={classGroups.map((item) => [item.id, item.display_name || item.name])} />
            <SelectField label="Subject" value={projectForm.class_subject} onChange={(value) => setProjectForm((prev) => ({ ...prev, class_subject: value }))} options={filteredClassSubjects.map((item) => [item.id, `${item.class_group_name} - ${item.subject_code} ${item.subject_name}`])} />
            <SelectField label="Teacher" value={projectForm.teacher} onChange={(value) => setProjectForm((prev) => ({ ...prev, teacher: value }))} options={teachers.map((item) => [item.id, item.full_name])} required={false} disabled={isTeacher} />
            <TextField label="Start Date" type="date" value={projectForm.start_date} onChange={(value) => setProjectForm((prev) => ({ ...prev, start_date: value }))} />
            <TextField label="Due Date" type="date" value={projectForm.due_date} onChange={(value) => setProjectForm((prev) => ({ ...prev, due_date: value }))} />
            <TextField label="Maximum Score" type="number" value={projectForm.max_score} onChange={(value) => setProjectForm((prev) => ({ ...prev, max_score: value }))} required />
            <SelectField label="Status" value={projectForm.status} onChange={(value) => setProjectForm((prev) => ({ ...prev, status: value }))} options={[["DRAFT", "Draft"], ["ACTIVE", "Active"], ["CLOSED", "Closed"], ["ARCHIVED", "Archived"]]} />
            <TextField label="External URL" value={projectForm.external_url} onChange={(value) => setProjectForm((prev) => ({ ...prev, external_url: value }))} placeholder="Google Drive, YouTube, Scratch link" />
            <div className="col-md-4"><label className="form-label fw-semibold">Attachment</label><input type="file" className="form-control" onChange={(event) => setProjectFile(event.target.files?.[0] || null)} /></div>
            <div className="col-12"><label className="form-label fw-semibold">Instructions</label><textarea className="form-control" rows="3" value={projectForm.instructions} onChange={(event) => setProjectForm((prev) => ({ ...prev, instructions: event.target.value }))}></textarea></div>
            <SaveBar loading={loading} label={projectForm.id ? "Update Project" : "Add Project"} onCancel={projectForm.id ? () => setProjectForm({ ...emptyProject, teacher: isTeacher ? currentUser?.id || "" : "" }) : null} />
          </form>

          <SimpleTable headers={["Project", "Type", "Class", "Subject", "Teacher", "Status", "Due", "Actions"]} rows={projects.map((project) => [project.title, project.project_type_label || project.project_type, project.class_group_name, project.subject_name, project.teacher_name || "-", <Status value={project.status_label || project.status} />, project.due_date || "-", <ActionButtons onEdit={() => editProject(project)} onDelete={() => deleteRecord(`ict-robotics/projects/${project.id}/`, project.title)} />])} />
        </SetupCard>
      )}

      {activeTab === "submissions" && (
        <SetupCard title="Student Submissions">
          <form className="row g-3 mb-4" onSubmit={saveSubmission}>
            <SelectField label="Project" value={submissionForm.project} onChange={(value) => setSubmissionForm((prev) => ({ ...prev, project: value, student: "" }))} options={projects.map((item) => [item.id, `${item.title} - ${item.class_group_name}`])} />
            <SelectField label="Student" value={submissionForm.student} onChange={(value) => setSubmissionForm((prev) => ({ ...prev, student: value }))} options={filteredStudents.map((item) => [item.id, `${item.student_number || ""} ${item.full_name}`])} />
            <SelectField label="Status" value={submissionForm.status} onChange={(value) => setSubmissionForm((prev) => ({ ...prev, status: value }))} options={[["NOT_SUBMITTED", "Not Submitted"], ["SUBMITTED", "Submitted"], ["REVIEWED", "Reviewed"], ["RESUBMIT", "Needs Resubmission"]]} />
            <TextField label="Score" type="number" value={submissionForm.score} onChange={(value) => setSubmissionForm((prev) => ({ ...prev, score: value }))} />
            <TextField label="Submission URL" value={submissionForm.submission_url} onChange={(value) => setSubmissionForm((prev) => ({ ...prev, submission_url: value }))} />
            <div className="col-md-4"><label className="form-label fw-semibold">Submitted File</label><input type="file" className="form-control" onChange={(event) => setSubmissionFile(event.target.files?.[0] || null)} /></div>
            <TextField label="Teacher Comment" value={submissionForm.teacher_comment} onChange={(value) => setSubmissionForm((prev) => ({ ...prev, teacher_comment: value }))} />
            <TextField label="Notes" value={submissionForm.notes} onChange={(value) => setSubmissionForm((prev) => ({ ...prev, notes: value }))} />
            <SaveBar loading={loading} label={submissionForm.id ? "Update Submission" : "Record Submission"} onCancel={submissionForm.id ? () => setSubmissionForm(emptySubmission) : null} />
          </form>
          <SimpleTable headers={["Project", "Student", "Class", "Status", "Score", "Reviewed By", "Actions"]} rows={submissions.map((item) => [item.project_title, `${item.student_number || ""} ${item.student_name}`, item.class_group_name, <Status value={item.status_label || item.status} />, item.score || "-", item.reviewed_by_name || "-", <ActionButtons onEdit={() => editSubmission(item)} onDelete={() => deleteRecord(`ict-robotics/submissions/${item.id}/`, item.student_name)} />])} />
        </SetupCard>
      )}

      {activeTab === "skills" && (
        <div className="row g-3">
          <div className="col-lg-4">
            <SetupCard title="Skills Library">
              <form className="row g-2 mb-3" onSubmit={saveSkill}>
                <TextField label="Skill Name" value={skillForm.name} onChange={(value) => setSkillForm((prev) => ({ ...prev, name: value }))} required />
                <TextField label="Code" value={skillForm.code} onChange={(value) => setSkillForm((prev) => ({ ...prev, code: value.toUpperCase() }))} required />
                <SelectField label="Skill Area" value={skillForm.skill_area} onChange={(value) => setSkillForm((prev) => ({ ...prev, skill_area: value }))} options={[["ICT", "ICT"], ["ROBOTICS", "Robotics"], ["CODING", "Coding"], ["DIGITAL_LITERACY", "Digital Literacy"], ["SOFT_SKILLS", "Soft Skills"]]} />
                <SaveBar loading={loading} label={skillForm.id ? "Update Skill" : "Add Skill"} onCancel={skillForm.id ? () => setSkillForm(emptySkill) : null} />
              </form>
              <SimpleTable headers={["Skill", "Area"]} rows={skills.map((skill) => [skill.name, skill.skill_area_label || skill.skill_area])} />
            </SetupCard>
          </div>
          <div className="col-lg-8">
            <SetupCard title="Student Skill Progress">
              <form className="row g-3 mb-4" onSubmit={saveProgress}>
                <SelectField label="Class Group" value={progressForm.class_group} onChange={(value) => setProgressForm((prev) => ({ ...prev, class_group: value, student: "" }))} options={classGroups.map((item) => [item.id, item.display_name || item.name])} required={false} />
                <SelectField label="Student" value={progressForm.student} onChange={(value) => setProgressForm((prev) => ({ ...prev, student: value }))} options={students.filter((item) => !progressForm.class_group || String(item.class_group) === String(progressForm.class_group)).map((item) => [item.id, `${item.student_number || ""} ${item.full_name}`])} />
                <SelectField label="Skill" value={progressForm.skill} onChange={(value) => setProgressForm((prev) => ({ ...prev, skill: value }))} options={skills.filter((item) => item.is_active).map((item) => [item.id, item.name])} />
                <SelectField label="Level" value={progressForm.level} onChange={(value) => setProgressForm((prev) => ({ ...prev, level: value }))} options={[["NOT_STARTED", "Not Started"], ["DEVELOPING", "Developing"], ["COMPETENT", "Competent"], ["ADVANCED", "Advanced"]]} />
                <SelectField label="Teacher" value={progressForm.teacher} onChange={(value) => setProgressForm((prev) => ({ ...prev, teacher: value }))} options={teachers.map((item) => [item.id, item.full_name])} required={false} disabled={isTeacher} />
                <TextField label="Assessed Date" type="date" value={progressForm.assessed_at} onChange={(value) => setProgressForm((prev) => ({ ...prev, assessed_at: value }))} />
                <TextField label="Evidence" value={progressForm.evidence} onChange={(value) => setProgressForm((prev) => ({ ...prev, evidence: value }))} />
                <TextField label="Notes" value={progressForm.notes} onChange={(value) => setProgressForm((prev) => ({ ...prev, notes: value }))} />
                <SaveBar loading={loading} label={progressForm.id ? "Update Progress" : "Save Progress"} onCancel={progressForm.id ? () => setProgressForm({ ...emptyProgress, teacher: isTeacher ? currentUser?.id || "" : "" }) : null} />
              </form>
              <SimpleTable headers={["Student", "Class", "Skill", "Level", "Teacher", "Date", "Actions"]} rows={progressRecords.map((item) => [item.student_name, item.class_group_name || "-", item.skill_name, <Status value={item.level_label || item.level} />, item.teacher_name || "-", item.assessed_at, <ActionButtons onEdit={() => editProgress(item)} onDelete={() => deleteRecord(`ict-robotics/student-skills/${item.id}/`, item.student_name)} />])} />
            </SetupCard>
          </div>
        </div>
      )}

      {activeTab === "equipment" && (
        <SetupCard title="Lab Equipment">
          <form className="row g-3 mb-4" onSubmit={saveEquipment}>
            <TextField label="Item Name" value={equipmentForm.name} onChange={(value) => setEquipmentForm((prev) => ({ ...prev, name: value }))} required />
            <SelectField label="Category" value={equipmentForm.category} onChange={(value) => setEquipmentForm((prev) => ({ ...prev, category: value }))} options={[["COMPUTER", "Computer / Laptop"], ["TABLET", "Tablet"], ["ROBOT_KIT", "Robot Kit"], ["SENSOR", "Sensor"], ["CABLE", "Cable"], ["PROJECTOR", "Projector"], ["SOFTWARE", "Software"], ["OTHER", "Other"]]} />
            <TextField label="Asset Tag" value={equipmentForm.asset_tag} onChange={(value) => setEquipmentForm((prev) => ({ ...prev, asset_tag: value }))} />
            <TextField label="Serial Number" value={equipmentForm.serial_number} onChange={(value) => setEquipmentForm((prev) => ({ ...prev, serial_number: value }))} />
            <TextField label="Condition" value={equipmentForm.condition} onChange={(value) => setEquipmentForm((prev) => ({ ...prev, condition: value }))} />
            <SelectField label="Status" value={equipmentForm.status} onChange={(value) => setEquipmentForm((prev) => ({ ...prev, status: value }))} options={[["AVAILABLE", "Available"], ["IN_USE", "In Use"], ["DAMAGED", "Damaged"], ["LOST", "Lost"], ["REPAIR", "Under Repair"], ["RETIRED", "Retired"]]} />
            <SelectField label="Assigned Teacher" value={equipmentForm.assigned_teacher} onChange={(value) => setEquipmentForm((prev) => ({ ...prev, assigned_teacher: value }))} options={teachers.map((item) => [item.id, item.full_name])} required={false} />
            <SelectField label="Assigned Class" value={equipmentForm.assigned_class_group} onChange={(value) => setEquipmentForm((prev) => ({ ...prev, assigned_class_group: value }))} options={classGroups.map((item) => [item.id, item.display_name || item.name])} required={false} />
            <TextField label="Purchase Date" type="date" value={equipmentForm.purchase_date} onChange={(value) => setEquipmentForm((prev) => ({ ...prev, purchase_date: value }))} />
            <TextField label="Notes" value={equipmentForm.notes} onChange={(value) => setEquipmentForm((prev) => ({ ...prev, notes: value }))} />
            <SaveBar loading={loading} label={equipmentForm.id ? "Update Equipment" : "Add Equipment"} onCancel={equipmentForm.id ? () => setEquipmentForm(emptyEquipment) : null} />
          </form>
          <SimpleTable headers={["Item", "Category", "Asset Tag", "Status", "Assigned To", "Class", "Actions"]} rows={equipment.map((item) => [item.name, item.category_label || item.category, item.asset_tag || "-", <Status value={item.status_label || item.status} />, item.assigned_teacher_name || "-", item.assigned_class_group_name || "-", <ActionButtons onEdit={() => setEquipmentForm({ ...emptyEquipment, ...item })} onDelete={() => deleteRecord(`ict-robotics/equipment/${item.id}/`, item.name)} />])} />
        </SetupCard>
      )}

      {activeTab === "sessions" && (
        <SetupCard title="Lab Sessions">
          <form className="row g-3 mb-4" onSubmit={saveSession}>
            <TextField label="Session Title" value={sessionForm.title} onChange={(value) => setSessionForm((prev) => ({ ...prev, title: value }))} required />
            <SelectField label="Class Group" value={sessionForm.class_group} onChange={(value) => setSessionForm((prev) => ({ ...prev, class_group: value, class_subject: "" }))} options={classGroups.map((item) => [item.id, item.display_name || item.name])} />
            <SelectField label="Subject" value={sessionForm.class_subject} onChange={(value) => setSessionForm((prev) => ({ ...prev, class_subject: value }))} options={filteredSessionSubjects.map((item) => [item.id, `${item.class_group_name} - ${item.subject_code} ${item.subject_name}`])} required={false} />
            <SelectField label="Project" value={sessionForm.project} onChange={(value) => setSessionForm((prev) => ({ ...prev, project: value }))} options={projects.map((item) => [item.id, item.title])} required={false} />
            <SelectField label="Teacher" value={sessionForm.teacher} onChange={(value) => setSessionForm((prev) => ({ ...prev, teacher: value }))} options={teachers.map((item) => [item.id, item.full_name])} required={false} disabled={isTeacher} />
            <TextField label="Lab Room" value={sessionForm.lab_room} onChange={(value) => setSessionForm((prev) => ({ ...prev, lab_room: value }))} />
            <TextField label="Date" type="date" value={sessionForm.session_date} onChange={(value) => setSessionForm((prev) => ({ ...prev, session_date: value }))} required />
            <TextField label="Start Time" type="time" value={sessionForm.start_time} onChange={(value) => setSessionForm((prev) => ({ ...prev, start_time: value }))} required />
            <TextField label="End Time" type="time" value={sessionForm.end_time} onChange={(value) => setSessionForm((prev) => ({ ...prev, end_time: value }))} required />
            <SelectField label="Status" value={sessionForm.status} onChange={(value) => setSessionForm((prev) => ({ ...prev, status: value }))} options={[["PLANNED", "Planned"], ["COMPLETED", "Completed"], ["CANCELLED", "Cancelled"]]} />
            <MultiSelectField label="Equipment Used" value={sessionForm.equipment} onChange={(value) => setSessionForm((prev) => ({ ...prev, equipment: value }))} options={equipment.map((item) => [item.id, item.name])} />
            <TextField label="Objectives" value={sessionForm.objectives} onChange={(value) => setSessionForm((prev) => ({ ...prev, objectives: value }))} />
            <TextField label="Session Notes" value={sessionForm.session_notes} onChange={(value) => setSessionForm((prev) => ({ ...prev, session_notes: value }))} />
            <SaveBar loading={loading} label={sessionForm.id ? "Update Session" : "Add Session"} onCancel={sessionForm.id ? () => setSessionForm({ ...emptySession, teacher: isTeacher ? currentUser?.id || "" : "" }) : null} />
          </form>
          <SimpleTable headers={["Date", "Session", "Class", "Subject", "Teacher", "Room", "Status", "Actions"]} rows={labSessions.map((item) => [item.session_date, item.title, item.class_group_name, item.subject_name || "-", item.teacher_name || "-", item.lab_room || "-", <Status value={item.status_label || item.status} />, <ActionButtons onEdit={() => editSession(item)} onDelete={() => deleteRecord(`ict-robotics/lab-sessions/${item.id}/`, item.title)} />])} />
        </SetupCard>
      )}
    </div>
  );
}

function cleanPayload(form) {
  const payload = {};
  Object.entries(form).forEach(([key, value]) => {
    if (key === "id") return;
    if (Array.isArray(value)) {
      payload[key] = value;
      return;
    }
    if (value !== "" && value !== null && value !== undefined) payload[key] = value;
  });
  return payload;
}

function showError(error, fallback) {
  const data = error.response?.data;
  const message = data?.detail || data?.non_field_errors?.[0] || Object.values(data || {})?.flat?.()?.[0] || fallback;
  toast.error(message);
}

function Summary({ label, value }) {
  return (
    <div className="col-6 col-xl-3">
      <div className="border rounded-3 p-3 bg-light h-100">
        <small className="text-muted fw-bold">{label}</small>
        <div className="fs-4 fw-bold" style={{ color: "#125b2d" }}>{value}</div>
      </div>
    </div>
  );
}

function DashboardLine({ label, value }) {
  return <div className="list-group-item d-flex justify-content-between px-0"><span>{label}</span><strong>{value}</strong></div>;
}

function SetupCard({ title, children }) {
  return <div className="card border-0 shadow-sm"><div className="card-body"><h5 className="fw-bold mb-3">{title}</h5>{children}</div></div>;
}

function TextField({ label, value, onChange, type = "text", placeholder = "", required = false }) {
  return (
    <div className="col-md-4">
      <label className="form-label fw-semibold">{label}</label>
      <input type={type} className="form-control" value={value || ""} placeholder={placeholder} onChange={(event) => onChange(event.target.value)} required={required} />
    </div>
  );
}

function SelectField({ label, value, onChange, options, required = true, disabled = false }) {
  return (
    <div className="col-md-4">
      <label className="form-label fw-semibold">{label}</label>
      <select className="form-select" value={value || ""} onChange={(event) => onChange(event.target.value)} required={required} disabled={disabled}>
        <option value="">Select {label}</option>
        {(options || []).map(([optionValue, optionLabel]) => <option key={optionValue} value={optionValue}>{optionLabel}</option>)}
      </select>
    </div>
  );
}

function MultiSelectField({ label, value, onChange, options }) {
  return (
    <div className="col-md-4">
      <label className="form-label fw-semibold">{label}</label>
      <select className="form-select" multiple value={(value || []).map(String)} onChange={(event) => onChange(Array.from(event.target.selectedOptions).map((option) => option.value))}>
        {(options || []).map(([optionValue, optionLabel]) => <option key={optionValue} value={optionValue}>{optionLabel}</option>)}
      </select>
      <small className="text-muted">Hold Ctrl to select multiple.</small>
    </div>
  );
}

function SaveBar({ loading, label, onCancel }) {
  return (
    <div className="col-12 d-flex gap-2">
      <button type="submit" className="btn btn-success" disabled={loading}>{loading ? "Saving..." : label}</button>
      {onCancel && <button type="button" className="btn btn-light border" onClick={onCancel}>Cancel Edit</button>}
    </div>
  );
}

function SimpleTable({ headers, rows }) {
  return (
    <div className="table-responsive border rounded-3">
      <table className="table table-sm align-middle mb-0">
        <thead className="table-light"><tr>{headers.map((header) => <th key={header}>{header}</th>)}</tr></thead>
        <tbody>
          {rows.length === 0 ? <tr><td colSpan={headers.length} className="text-center text-muted py-4">No records found.</td></tr> : rows.map((row, index) => <tr key={index}>{row.map((cell, cellIndex) => <td key={cellIndex}>{cell}</td>)}</tr>)}
        </tbody>
      </table>
    </div>
  );
}

function Status({ value }) {
  const text = value || "-";
  const normalized = String(text).toUpperCase();
  const badge = normalized.includes("ACTIVE") || normalized.includes("AVAILABLE") || normalized.includes("COMPETENT") || normalized.includes("ADVANCED") || normalized.includes("REVIEWED") || normalized.includes("COMPLETED") ? "text-bg-success" : normalized.includes("DRAFT") || normalized.includes("PLANNED") || normalized.includes("DEVELOPING") || normalized.includes("SUBMITTED") ? "text-bg-warning" : "text-bg-secondary";
  return <span className={`badge ${badge}`}>{text}</span>;
}

function ActionButtons({ onEdit, onDelete }) {
  return (
    <div className="d-flex gap-1">
      <button type="button" className="btn btn-sm btn-light border" onClick={onEdit}><i className="bi bi-pencil-square text-success"></i></button>
      <button type="button" className="btn btn-sm btn-light border" onClick={onDelete}><i className="bi bi-trash text-danger"></i></button>
    </div>
  );
}

export default ICTRobotics;
